import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  useTheme,
  Button,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { getHome } from "../services/api/homeApi";

export default function RealStories() {
  const theme = useTheme();
  const [isPlaying, setIsPlaying] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchHome();
  }, []);

  const fetchHome = async () => {
    try {
      const data = await getHome();
      setHomeData(data);
    } catch (error) {
      console.error("Failed to fetch home data:", error);
    }
  };

  // ✅ TRUE INFINITE SCROLL (NO GAP, NO JUMP)
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let frame;
    const speed = 0.5; // 🔥 adjust speed here

    const scroll = () => {
      if (container.scrollTop >= container.scrollHeight / 2) {
        container.scrollTop = 0; // seamless reset
      } else {
        container.scrollTop += speed;
      }
      frame = requestAnimationFrame(scroll);
    };

    if (isPlaying) {
      frame = requestAnimationFrame(scroll);
    }

    return () => cancelAnimationFrame(frame);
  }, [isPlaying, homeData]);

  if (!homeData) return <Typography>Loading Real Stories...</Typography>;

  return (
    <Box sx={{ pt: { xs: 6, md: 9 }, pb: 3 }}>
      <Container maxWidth="xl" sx={{ px: 2 }} disableGutters>
        <Box sx={{ maxWidth: "1440px", mx: { xs: 1, sm: 2, lg: "auto" } }}>
          <Box
            sx={{
              position: "relative",
              borderRadius: "36px",
              overflow: "hidden",
              minHeight: { xs: 150, md: 560 },
              backgroundImage: homeData.real_story_image
                ? `url(${homeData.real_story_image})`
                : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {/* PLAY / PAUSE */}
            <Button
              onClick={() => setIsPlaying(!isPlaying)}
              sx={{
                position: "absolute",
                top: 20,
                left: 20,
                zIndex: 10,
                background:
                  theme?.custom?.colors?.overlay?.whiteLight ||
                  "rgba(255,255,255,0.7)",
                color: "primary.contrastText",
                fontWeight: 600,
              }}
            >
              {isPlaying ? "Pause" : "Play"}
            </Button>

            {/* CONTENT */}
            <Box sx={{ px: { xs: 3, md: 10 }, pt: { xs: 7, md: 0 }, pb: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  gap: { xs: 4, md: 6, lg: 8 },
                }}
              >
                {/* LEFT */}
                <Box sx={{ flex: 1, width: "100%" }}>
                  <Typography
                    sx={{
                      fontSize: { xs: 30, md: 54 },
                      fontWeight: 700,
                      color: "primary.contrastText",
                      lineHeight: 1.05,
                      mb: 3,
                    }}
                  >
                    {homeData.real_story_title}
                  </Typography>

                  <Typography
                    sx={{
                      color: "primary.contrastText",
                      maxWidth: 460,
                      mb: 4,
                      lineHeight: 1.7,
                    }}
                  >
                    {homeData.real_story_desc}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        sx={{
                          color:
                            theme?.custom?.colors?.rating?.main || "#FFD700",
                        }}
                      />
                    ))}
                    <Typography sx={{ color: "primary.contrastText" }}>
                      2,000+ Ratings
                    </Typography>
                  </Stack>
                </Box>

                {/* RIGHT */}
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-end" },
                  }}
                >
                  <Box
                    ref={scrollRef}
                    onWheel={(e) => e.preventDefault()}
                    onTouchMove={(e) => e.preventDefault()}
                    sx={{
                      width: "100%",
                      maxWidth: 460,
                      height: { xs: 420, md: 580 },
                      overflow: "hidden",
                      position: "relative",
                      scrollBehavior: "auto",
                      display: "flex", // 🔥 important
                      flexDirection: "column",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        justifyContent: "space-between",
                        minHeight: "100%", // 🔥 fill full height
                        "& > *": {
                          flexShrink: 0, // 🔥 prevent shrinking
                        },
                      }}
                    >
                      {[...homeData.real_story, ...homeData.real_story].map(
                        (item, i) => (
                          <Box
                            key={i}
                            sx={{
                              p: 3,
                              borderRadius: "22px",
                              background: "rgba(255, 255, 255, 0.15)", // 🔥 more visible
                              backdropFilter: "blur(20px)",            // 🔥 blur effect
                              WebkitBackdropFilter: "blur(10px)",      // 🔥 safari support
                              border: "1px solid rgba(255,255,255,0.2)", // optional premium look
                              color:
                                theme?.custom?.colors?.contrastText || "#fff",
                              minHeight: 180,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography fontSize={14.5} lineHeight={1.6}>
                              {item.reviewer_text}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography fontWeight={600}>
                                {item.reviewer_name}
                              </Typography>

                              <Box>
                                {[...Array(5)].map((_, i) => (
                                  <StarIcon
                                    key={i}
                                    sx={{
                                      color:
                                        theme?.custom?.colors?.rating?.main ||
                                        "#FFD700",
                                      fontSize: 16,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}